import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@monksflow/monks-ui';
import { ArtifactsService } from '@inference.engine/api-client/web';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { inferenceHttp } from '@/lib/api/http-client';
import { ArtifactPreviewFallback } from './ArtifactPreviewFallback';
import { ArtifactPreviewWidget } from './ArtifactPreviewWidget';

interface Props {
  artifactId: string;
  /** Live widget registry key the saved snapshot is mounted through. */
  widgetKey: string;
}

/**
 * Renders a saved (context-less) widget artifact in the Library preview.
 *
 * Context-less artifacts carry no api enrichment on the workspace-api detail
 * path (its `data` is `{}`), so the snapshot is read directly from the
 * inference-api by id (mirrors `OpportunityArtifactPreview`), then mounted
 * through the live widget via `ArtifactPreviewWidget` using `widgetKey`.
 */
export function SavedWidgetArtifactPreview({ artifactId, widgetKey }: Props) {
  const workspaceId = useCurrentWorkspaceId();

  if (!workspaceId) {
    return <ArtifactPreviewFallback />;
  }

  return (
    <ResolvedSavedWidgetPreview
      workspaceId={workspaceId}
      artifactId={artifactId}
      widgetKey={widgetKey}
    />
  );
}

function ResolvedSavedWidgetPreview({
  workspaceId,
  artifactId,
  widgetKey,
}: {
  workspaceId: string;
  artifactId: string;
  widgetKey: string;
}) {
  const query = useQuery({
    queryKey: ['saved-widget-artifact-snapshot', workspaceId, artifactId],
    queryFn: async () => {
      const response = await ArtifactsService.getWorkspaceArtifactById({
        client: inferenceHttp,
        path: { workspaceId, artifactId },
      });
      if (!response.data) {
        throw new Error('No data returned from workspace artifact API');
      }
      // Raw snapshot payload; ArtifactPreviewWidget mounts it through the registry,
      // which normalizes widget input — no schema parse at this boundary.
      return response.data.data as Record<string, unknown>;
    },
    staleTime: Infinity,
  });

  if (query.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (query.isError) {
    return <ArtifactPreviewFallback />;
  }

  return (
    <ArtifactPreviewWidget
      artifactKey={widgetKey}
      data={query.data}
      fallback={<ArtifactPreviewFallback />}
    />
  );
}
